// src/pages/EditMarketConditions.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";




const safeParseFloat = (value: string): number | null => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

interface Competitor {
  name: string;
  share: string;
}

interface MarketConditionsData {
  id: number | null;
  cropLoss: string;
  cropInsurance: string;
  returns: string;
  governmentSubsidies: string;
  ourCompanyShare: string;
  competitors: Competitor[];
  whyChooseUs: string;
  whyChooseCompetitors: string;
}

interface Report { id: number; created_at: string; }

const EditMarketConditions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<MarketConditionsData>({
    id: null,
    cropLoss: "",
    cropInsurance: "",
    returns: "",
    governmentSubsidies: "",
    ourCompanyShare: "",
    competitors: [
      { name: "", share: "" },
      { name: "", share: "" },
      { name: "", share: "" }
    ],
    whyChooseUs: "",
    whyChooseCompetitors: ""
  });
  const [reports, setReports] = useState<Report[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Load or create report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setReports(data);
          setNewestReportId(data[0].id);
        } else {
          fetch(`/api/dealers/${id}/reports`, { method: "POST", headers: { "Content-Type": "application/json" } })
            .then(r => r.json())
            .then(nr => setNewestReportId(nr.id));
        }
      });
  }, [id]);

  // Load existing market conditions
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/market-conditions`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const rec = data[0];
          setFormData(fd => ({
            ...fd,
            id: rec.id,
            cropLoss: rec.crop_loss?.toString() ?? "",
            cropInsurance: rec.crop_insurance ? "Yes" : "No",
            returns: rec.returns?.toString() ?? "",
            governmentSubsidies: rec.government_subsidies || "",
             ourCompanyShare: rec.our_company_share?.toString() || "",
            competitors: [
              { name: rec.competitor1_name || "", share: rec.competitor1_share?.toString() || "" },
              { name: rec.competitor2_name || "", share: rec.competitor2_share?.toString() || "" },
              { name: rec.competitor3_name || "", share: rec.competitor3_share?.toString() || "" }
            ],
            whyChooseUs: rec.why_choose_us || "",
            whyChooseCompetitors: rec.why_choose_competitors || ""
          }));
        }
      });
  }, [newestReportId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value } as any));
  };

  const handleDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value } as any));
  };

  const handleCompetitorChange = (idx: number, field: keyof Competitor, value: string) => {
    setFormData(fd => {
      const comps = [...fd.competitors];
      comps[idx] = { ...comps[idx], [field]: value };
      return { ...fd, competitors: comps };
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (newestReportId == null) return;
	
	const isNew = formData.id == null; 
	
    const payload: any = {
      id: formData.id,
      crop_loss: safeParseFloat(formData.cropLoss),
      crop_insurance: formData.cropInsurance === "Yes",
      returns: safeParseFloat(formData.returns),
      government_subsidies: formData.governmentSubsidies,
      our_company_share: safeParseFloat(formData.ourCompanyShare),
      competitor1_name: formData.competitors[0].name || null,
      competitor1_share: safeParseFloat(formData.competitors[0].share),
      competitor2_name: formData.competitors[1].name || null,
      competitor2_share: safeParseFloat(formData.competitors[1].share),
      competitor3_name: formData.competitors[2].name || null,
      competitor3_share: safeParseFloat(formData.competitors[2].share),
      why_choose_us: formData.whyChooseUs,
      why_choose_competitors: formData.whyChooseCompetitors
    };
    fetch(`/api/reports/${newestReportId}/market-conditions`, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => { throw new Error(err.error || JSON.stringify(err)); });
        }
        navigate(`/dealer/${id}`);
      })
      .catch(err => console.error("Error updating market conditions:", err));
  };

  const sectionHeader: React.CSSProperties = {
    color: "#e41e26",
    borderBottom: "1px solid #e41e26",
    paddingBottom: "5px",
    marginTop: "15px",
    fontSize: "16px"
  };

  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc", boxSizing: "border-box" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={{ backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" }}>
      <Navbar />
      <div style={{ backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "800px", margin: "30px auto", padding: "30px" }}>
        <h1 style={{ textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" }}>
          Edit Market Conditions
        </h1>
        <form onSubmit={handleSubmit}>
          <h3 style={sectionHeader}>Crop Loss & Insurance</h3>
          <label style={{ fontWeight: "bold" }}>Crop Loss (%):</label>
          <input type="text" name="cropLoss" value={formData.cropLoss} onChange={handleChange} placeholder="Enter crop loss %" style={inputStyle} />
          <label style={{ fontWeight: "bold" }}>Crop Insurance:</label>
          <select name="cropInsurance" value={formData.cropInsurance} onChange={handleDropdown} style={inputStyle}>
            <option value="">Select…</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          <h3 style={sectionHeader}>Returns & Subsidies</h3>
                    <label style={{ fontWeight: "bold" }}>Returns on Crops (%):</label>
          <input
            type="text"
            name="returns"
            value={formData.returns}
            onChange={handleChange}
            placeholder="Enter % (e.g. 5)"
            style={inputStyle}
          />
          <label style={{ fontWeight: "bold" }}>Government Subsidies (Explain):</label>
          <textarea name="governmentSubsidies" value={formData.governmentSubsidies} onChange={handleChange} placeholder="Explain subsidies…" style={{ ...inputStyle, height: "100px" }} />

          <h3 style={sectionHeader}>Market Share</h3>
 
          
          <label style={{ fontWeight: "bold" }}>Orchard-Rite Market Share (%):</label>
          <input type="text" name="ourCompanyShare" value={formData.ourCompanyShare} onChange={handleChange} placeholder="Enter %" style={inputStyle} />
          {formData.competitors.map((comp, idx) => (
            <div key={idx} style={{ marginBottom: "10px" }}>
              <label style={{ fontWeight: "bold" }}>Competitor #{idx + 1} Name:</label>
              <input type="text" value={comp.name} onChange={e => handleCompetitorChange(idx, 'name', e.target.value)} placeholder="Enter name (optional)" style={inputStyle} />
              <label style={{ fontWeight: "bold" }}>Market Share (%):</label>
              <input type="text" value={comp.share} onChange={e => handleCompetitorChange(idx, 'share', e.target.value)} placeholder="Enter % (optional)" style={inputStyle} />
            </div>
          ))}

          <h3 style={sectionHeader}>Customer Preferences</h3>
          <label style={{ fontWeight: "bold" }}>Why Customers Choose Us:</label>
          <textarea name="whyChooseUs" value={formData.whyChooseUs} onChange={handleChange} placeholder="Enter reasons…" style={{ ...inputStyle, height: "100px" }} />
          <label style={{ fontWeight: "bold" }}>Why Customers Choose Competitors:</label>
          <textarea name="whyChooseCompetitors" value={formData.whyChooseCompetitors} onChange={handleChange} placeholder="Enter reasons…" style={{ ...inputStyle, height: "100px" }} />

          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMarketConditions;
